import { useState, useCallback, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Fab,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { createWorkflow, listAgents, listTools, configureWorkflowSchedule, configureWorkflowWebhook } from "../../api/agentApi";
import { useNavigate } from "react-router-dom";
import {
  ReactFlow,
  addEdge,
  Background,
  BackgroundVariant,
  Controls,
  useNodesState,
  useEdgesState,
  Panel,
  Node,
  Edge,
  OnConnect,
  Connection,
} from "reactflow";
import AddIcon from "@mui/icons-material/Add";
import "reactflow/dist/style.css";
import WorkflowNode, { WorkflowNodeData } from "./WorkflowNode.tsx";
import WorkflowPropertiesDrawer from "./WorkflowPropertiesDrawer.tsx";
import { AddNodePopover } from "./AddNodePopover.tsx";
import { ConnectionDialog } from "./ConnectionDialog.tsx";
import { SmartConnectionDialog, ConnectionConfig } from "./SmartConnectionDialog.tsx";
import { CronScheduleBuilder, getScheduleLabel } from "./CronScheduleBuilder.tsx";
import { getWebhookLabel } from "./WebhookConfigBuilder.tsx";
import { convertNodesToSteps, getTriggerNodes, handleNodeTypeSelection, sanitizeWorkflowName, createWorkflowNode, STRUCTURAL_NODE_DEFAULT_LABELS, computeDataFlowEdges } from "./workflowUtils.ts";

const nodeTypes = { muiNode: WorkflowNode };

/**
 * localStorage Key for Draft Persistence
 * 
 * Purpose: Auto-save unsaved workflow changes to prevent data loss
 * Storage Duration: Persists indefinitely until:
 *   1. User successfully saves the workflow (cleared on save)
 *   2. User manually clears browser data
 *   3. Draft restoration fails (corrupted JSON, then auto-deleted)
 * 
 * Data Stored: { version, nodes, edges, workflowName, workflowDescription, savedAt }
 * Scope: Per-browser, per-domain (not shared across devices/browsers)
 * Size: Typically <1MB for reasonable workflows (localStorage limit ~5-10MB)
 */
const WORKFLOW_DRAFT_STORAGE_KEY = "nextmoca.workflowGeneratorDraft";

const WorkflowGeneratorPage: React.FC = () => {
  const navigate = useNavigate();

  const [nodes, setNodes, onNodesChange] = useNodesState<WorkflowNodeData>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  // UI State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [popoverStep, setPopoverStep] = useState<"select" | "config">("select");

  // Form State

  const [formData, setFormData] = useState<{
    id: string;
    version: string;
    prompt: string;
    triggerType: "schedule" | "webhook";
    cron: string;
    show_status?: boolean;
    webhookConfig: {
      adapter_type: string;
      secrets: Record<string, string>;
    };
  }>({
    id: "",
    version: "1",
    prompt: "",
    triggerType: "schedule", // Default trigger type
    cron: "", // Initialize as empty string
    show_status: false,
    webhookConfig: {
      adapter_type: "slack",
      secrets: {},
    },
  });

  const [agents, setAgents] = useState<any[]>([]);
  const [tools, setTools] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Connection Dialog State
  const [connectionDialogOpen, setConnectionDialogOpen] = useState(false);
  const [pendingConnection, setPendingConnection] = useState<Connection | null>(
    null
  );
  const [selectedSourceIds, setSelectedSourceIds] = useState<string[]>([]);

  // Smart Connection Dialog State
  const [smartDialogOpen, setSmartDialogOpen] = useState(false);
  const [smartDialogSources, setSmartDialogSources] = useState<Node<WorkflowNodeData>[]>([]);
  const [smartDialogTarget, setSmartDialogTarget] = useState<Node<WorkflowNodeData> | null>(null);
  // const [inputMappings, setInputMappings] = useState<{ key: string; value: string }[]>([]); // Deprecated

  // Workflow Metadata State
  const [workflowName, setWorkflowName] = useState("New Workflow");
  const [workflowDisplayName, setWorkflowDisplayName] = useState("New Workflow");
  const [workflowDescription, setWorkflowDescription] = useState("");
  const [globalTimeoutSeconds, setGlobalTimeoutSeconds] = useState<number>(1800);
  const [isSaving, setIsSaving] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  // Trigger Config Retry State
  const [triggerRetryDialogOpen, setTriggerRetryDialogOpen] = useState(false);
  const [failedTriggerWorkflowId, setFailedTriggerWorkflowId] = useState<string | null>(null);
  const [failedTriggerConfig, setFailedTriggerConfig] = useState<{
    cron: string;
    error: string;
  } | null>(null);
  const [retryingTrigger, setRetryingTrigger] = useState(false);

  // Snackbar State
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "info" | "warning" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  // Property Drawer State
  const [selectedElement, setSelectedElement] = useState<
    Node<WorkflowNodeData> | Edge | null
  >(null);
  // This holds 'agent', 'tool', 'trigger' or null if nothing is selected yet
  const [selectedType, setSelectedType] = useState<
    "agent" | "tool" | "trigger" | null
  >(null);

  const [drawerOpen, setDrawerOpen] = useState(false);

  // Toggle for showing input_mapping data flow edges
  const [showDataMappings, setShowDataMappings] = useState(false);

  // Compute data flow edges from nodes' input_mapping (for the toggle)
  const dataFlowEdges = useMemo(
    () => computeDataFlowEdges(nodes, edges),
    [nodes, edges]
  );

  // Edges to display: always show structural edges, conditionally show data flow
  const displayedEdges = useMemo(
    () => showDataMappings ? [...edges, ...dataFlowEdges] : edges,
    [edges, dataFlowEdges, showDataMappings]
  );

  const showSnackbar = (message: string, severity: "success" | "info" | "warning" | "error" = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: Node<WorkflowNodeData>) => {
      setSelectedElement(node);
      setDrawerOpen(true);
    },
    []
  );

  const onEdgeClick = useCallback((event: React.MouseEvent, edge: Edge) => {
    setSelectedElement(edge);
    setDrawerOpen(true);
  }, []);

  const onPaneClick = useCallback(() => {
    // Optional: Close drawer when clicking background?
    // setDrawerOpen(false);
    // setSelectedElement(null);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [a, t] = await Promise.all([listAgents(), listTools()]);
        const agentList = Array.isArray(a) ? a : a?.agents || [];
        // Sort agents by date descending (newest first)
        const sortedAgents = agentList.sort((x: any, y: any) => {
          const dateX = new Date(x.date || 0).getTime();
          const dateY = new Date(y.date || 0).getTime();
          return dateY - dateX;
        });
        setAgents(sortedAgents);
        setTools(Array.isArray(t) ? t : t?.tools || []);
      } catch (err) {
        setError("Failed to fetch data");
      }
    };
    loadData();
  }, []);

  /**
   * RESTORE DRAFT FROM localStorage ON PAGE LOAD
   * 
   * Runs once when component mounts to recover any unsaved work.
   * 
   * Use Cases:
   * - Browser crash recovery
   * - Accidental tab close recovery
   * - Navigation away and back recovery
   * 
   * Behavior:
   * - Silently fails if no draft exists (normal first visit)
   * - Auto-deletes corrupted drafts (invalid JSON)
   * - Restores: nodes, edges, workflowName, workflowDescription
   * 
   * Note: This does NOT restore form state (selectedType, formData, etc.)
   *       Only the canvas state and metadata are restored.
   */
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      const stored = window.localStorage.getItem(WORKFLOW_DRAFT_STORAGE_KEY);
      if (!stored) {
        return;
      }

      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed?.nodes)) {
        setNodes(parsed.nodes);
      }
      if (Array.isArray(parsed?.edges)) {
        setEdges(parsed.edges);
      }
      if (typeof parsed?.workflowName === "string") {
        setWorkflowName(parsed.workflowName);
      }
      if (typeof parsed?.workflowDisplayName === "string") {
        setWorkflowDisplayName(parsed.workflowDisplayName);
      }
      if (typeof parsed?.workflowDescription === "string") {
        setWorkflowDescription(parsed.workflowDescription);
      }
      if (typeof parsed?.globalTimeoutSeconds === "number") {
        setGlobalTimeoutSeconds(parsed.globalTimeoutSeconds);
      }
    } catch (draftError) {
      console.warn("Failed to restore workflow draft:", draftError);
      window.localStorage.removeItem(WORKFLOW_DRAFT_STORAGE_KEY);
    }
  }, [setNodes, setEdges]);

  /**
   * AUTO-SAVE DRAFT TO localStorage ON CHANGES
   * 
   * Triggers: Whenever nodes, edges, workflowName, or workflowDescription change
   * Frequency: Synchronous on every state change (no debouncing needed - localStorage is fast)
   * 
   * Draft Content Detection:
   * - Has nodes (user added agent/tool/trigger steps)
   * - Has edges (user connected steps)
   * - Workflow name changed from default "New Workflow"
   * - Workflow description is not empty
   * 
   * If ALL of the above are false (empty state), the draft is DELETED.
   * This prevents storing useless empty drafts.
   * 
   * Storage Format:
   * {
   *   version: 1,              // Schema version (for future migrations)
   *   nodes: Node[],           // ReactFlow nodes with all config
   *   edges: Edge[],           // ReactFlow edges (connections)
   *   workflowName: string,    // User-entered name
   *   workflowDescription: string,  // User-entered description
   *   savedAt: ISO8601 string  // Timestamp (for debugging/display)
   * }
   * 
   * Persistence Duration: Indefinite until explicitly cleared (see WORKFLOW_DRAFT_STORAGE_KEY comment)
   */
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const hasDraftContent =
      nodes.length > 0 ||
      edges.length > 0 ||
      workflowName.trim() !== "New Workflow" ||
      workflowDisplayName.trim() !== "New Workflow" ||
      workflowDescription.trim().length > 0;

    if (!hasDraftContent) {
      window.localStorage.removeItem(WORKFLOW_DRAFT_STORAGE_KEY);
      return;
    }

    const payload = {
      version: 1,
      nodes,
      edges,
      workflowName,
      workflowDisplayName,
      workflowDescription,
      globalTimeoutSeconds,
      savedAt: new Date().toISOString(),
    };

    window.localStorage.setItem(
      WORKFLOW_DRAFT_STORAGE_KEY,
      JSON.stringify(payload)
    );
  }, [nodes, edges, workflowName, workflowDisplayName, workflowDescription, globalTimeoutSeconds]);

  const defaultEdgeOptions = {
    type: "default", // Or 'default' for standard Bezier curves
    animated: false,
    // style: { strokeWidth: 2, stroke: "#94a3b8" },
  };

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(e.currentTarget);
  const handleClose = () => {
    setAnchorEl(null);
    setPopoverStep("select");
    setSelectedType(null);
    setFormData({
      id: "",
      version: "1",
      prompt: "",
      triggerType: "schedule",
      cron: "",
      webhookConfig: {
        adapter_type: "slack",
        secrets: {},
      },
    });
  };

  const handleTypeSelect = (optionId: string) => {
    const { selectedType: newType, formData: newFormData } = handleNodeTypeSelection(optionId, formData);
    setSelectedType(newType);
    setFormData(newFormData);
    setPopoverStep("config");
  };

  const startConfig = (type: "agent" | "tool") => {
    setSelectedType(type);
    setPopoverStep("config");
  };

  const onConnect: OnConnect = useCallback(
    (params: Connection) => {
      const sourceNode = nodes.find((n) => n.id === params.source);
      const targetNode = nodes.find((n) => n.id === params.target);

      // 1. Validation: Prevent connecting TO a trigger
      if (targetNode?.data.type === "trigger") {
        showSnackbar("Cannot connect to a trigger node", "error");
        return;
      }

      // 2. Specialized logic for Trigger Sources
      if (sourceNode?.data.type === "trigger") {
        // Find all non-trigger nodes
        const nonTriggerNodes = nodes.filter(n => n.data.type !== "trigger");
        
        // If there are no non-trigger nodes, don't allow connection
        if (nonTriggerNodes.length === 0) {
          showSnackbar("Add an agent or tool step before connecting triggers", "warning");
          return;
        }

        // Find the first non-trigger node (node with no dependencies or first added)
        // The "first" node should have no incoming edges or be the one with the earliest creation
        const existingEdges = edges;
        const nodesWithIncomingEdges = new Set(existingEdges.map(e => e.target));
        
        // Find nodes without any incoming edges (potential entry points)
        const entryNodes = nonTriggerNodes.filter(n => !nodesWithIncomingEdges.has(n.id));
        
        // Determine the first non-trigger node
        let firstNonTriggerNode: Node<WorkflowNodeData> | undefined;
        if (entryNodes.length > 0) {
          // If there are entry nodes, use the first one (by position or ID)
          firstNonTriggerNode = entryNodes[0];
        } else {
          // If all nodes have incoming edges, use the first non-trigger node
          firstNonTriggerNode = nonTriggerNodes[0];
        }

        // Validate that trigger is connecting to the first non-trigger node
        if (targetNode?.id !== firstNonTriggerNode?.id) {
          showSnackbar(
            `Triggers must connect to the first step in the workflow (${firstNonTriggerNode?.data.label || 'first node'})`,
            "error"
          );
          return;
        }

        // Check if this trigger is already connected to a node
        const existingTriggerConnection = edges.find(
          e => e.source === sourceNode.id
        );
        
        if (existingTriggerConnection) {
          showSnackbar("This trigger is already connected", "warning");
          return;
        }

        // Just add the visual edge to show the flow
        setEdges((eds) => addEdge({ ...params, type: "default" }, eds));

        // We explicitly DO NOT update setNodes here.
        // This ensures depends_on and input_mapping remain empty for this connection.
        showSnackbar(`Trigger connected to ${targetNode.data.label}`, "success");
        return;
      }

      // 3. Standard logic for Agent/Tool connections (Show Smart Popup)
      const source = nodes.find(n => n.id === params.source);
      const target = nodes.find(n => n.id === params.target);

      setPendingConnection(params);
      setSmartDialogSources(source ? [source] : []);
      setSmartDialogTarget(target || null);
      setSmartDialogOpen(true);
    },
    [nodes, edges, setEdges, showSnackbar]
  );

  const handleSmartConnectionSave = useCallback((config: ConnectionConfig) => {
    if (!pendingConnection) return;

    // Update target node with new config
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === pendingConnection.target) {
          const updated = { ...node };
          const existingData = updated.data.rawData || {};

          // Merge input mappings
          const updatedInputMapping = {
            ...(existingData.input_mapping || {}),
            ...config.inputMapping,
          };

          // Update rawData with new configurations
          updated.data = {
            ...updated.data,
            rawData: {
              ...existingData,
              input_mapping: updatedInputMapping,
              ...(config.promptUpdates && { prompt: config.promptUpdates }),
              ...(config.dataToDisplay && { data_to_display: config.dataToDisplay }),
            },
          };

          // Update routing config if conditions changed
          if (config.conditionUpdates && updated.data.rawData.routing_config) {
            const routingConfig = { ...updated.data.rawData.routing_config };
            const branches = routingConfig.branches || [];

            config.conditionUpdates.forEach(({ branchId, condition }) => {
              const branchIdx = branches.findIndex((b: any) => b.id === branchId);
              if (branchIdx !== -1) {
                branches[branchIdx] = { ...branches[branchIdx], condition };
              }
            });

            updated.data.rawData = {
              ...updated.data.rawData,
              routing_config: { ...routingConfig, branches },
            };
          }

          return updated;
        }
        return node;
      })
    );

    // Update depends_on for target node
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === pendingConnection.target) {
          const currentDeps = node.data.rawData?.depends_on || [];
          const sourceId = pendingConnection.source;
          if (sourceId && !currentDeps.includes(sourceId)) {
            return {
              ...node,
              data: {
                ...node.data,
                rawData: {
                  ...node.data.rawData,
                  depends_on: [...currentDeps, sourceId],
                },
              },
            };
          }
        }
        return node;
      })
    );

    // Add visual edge
    setEdges((eds) => addEdge({ ...pendingConnection, type: "default" }, eds));

    setSmartDialogOpen(false);
    showSnackbar("Connection configured successfully", "success");
  }, [pendingConnection, setNodes, setEdges, showSnackbar]);

  const handleRetryTriggerConfig = async () => {
    if (!failedTriggerWorkflowId || !failedTriggerConfig) return;
    
    setRetryingTrigger(true);
    try {
      console.log("Retrying schedule trigger config:", { 
        workflowId: failedTriggerWorkflowId, 
        cronExpression: failedTriggerConfig.cron 
      });
      
      await configureWorkflowSchedule(failedTriggerWorkflowId, {
        cron_expression: failedTriggerConfig.cron,
        enabled: true
      });
      
      console.log("✅ Schedule trigger configured successfully");
      setTriggerRetryDialogOpen(false);
      
      // Show success snackbar and navigate
      showSnackbar("Schedule trigger configured successfully!", "success");
      navigate(`/workflow/${failedTriggerWorkflowId}`);
      
      setFailedTriggerWorkflowId(null);
      setFailedTriggerConfig(null);
      
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      console.error("Retry failed:", errorMsg);
      
      // Update the error message in the dialog
      setFailedTriggerConfig(prev => prev ? { ...prev, error: errorMsg } : null);
      
      alert(`Failed to configure schedule trigger: ${errorMsg}`);
    } finally {
      setRetryingTrigger(false);
    }
  };

  const handleSkipTriggerConfig = () => {
    setTriggerRetryDialogOpen(false);

    if (failedTriggerWorkflowId) {
      showSnackbar("Workflow created successfully. You can configure the schedule trigger manually.", "info");
      navigate(`/workflow/${failedTriggerWorkflowId}`);
    }

    setFailedTriggerWorkflowId(null);
    setFailedTriggerConfig(null);
  };

  const calculateGlobalTimeout = useCallback(() => {
    let total = 0;
    nodes.forEach(node => {
      if (node.data.type === 'trigger') return;
      total += node.data.rawData?.timeout_seconds || node.data.config?.timeout_seconds || 300;
      if (node.data.type === 'human_input') {
        total += (node.data.config?.timeout_minutes || node.data.rawData?.timeout_minutes || 0) * 60;
      }
      total += 300; // 5 min grace per step
    });
    return Math.min(Math.max(total, 1800), 86400);
  }, [nodes]);

  const handleSaveWorkflow = async () => {
    setIsSaving(true);
    let createdWorkflowId: string | null = null;

    try {
      // Use utility function to convert nodes to steps (pass edges to populate depends_on)
      const steps = convertNodesToSteps(nodes, edges);

      // Validate that we have at least one step (not just triggers)
      if (steps.length === 0) {
        alert("Workflow must have at least one Agent or Tool step. Triggers alone are not sufficient.");
        setIsSaving(false);
        return;
      }

      // Sanitize workflow name to match backend schema requirements
      const sanitizedName = sanitizeWorkflowName(workflowName || workflowDisplayName);

      // Construct the Workflow Payload (Excluding Triggers)
      const dagDefinition = {
        workflow_name: sanitizedName,
        display_name: workflowDisplayName || sanitizedName,
        description: workflowDescription || "",
        steps: steps, // Only contains Agents and Tools
        global_timeout_seconds: Math.min(globalTimeoutSeconds, 86400),
      };

      console.log("Saving Workflow (Steps only):", dagDefinition);
      console.log("JSON payload:", JSON.stringify(dagDefinition, null, 2));
      console.log("Original name:", workflowName, "→ Sanitized:", sanitizedName);
      
      // Step 1: Create the workflow
      const workflowResponse = await createWorkflow(sanitizedName, dagDefinition);
      createdWorkflowId = workflowResponse.workflow_id;
      
      console.log("✅ Workflow created successfully:", createdWorkflowId);

      // Step 2: Configure schedule triggers (if any)
      const triggerNodes = getTriggerNodes(nodes);
      if (triggerNodes.length > 0) {
        const scheduleTriggers = triggerNodes.filter(node => node.data.config.triggerType === "schedule");
        
        // Configure schedule triggers via API
        for (const triggerNode of scheduleTriggers) {
          const cronExpression = triggerNode.data.config.cron;
          if (cronExpression) {
            try {
              console.log("Configuring schedule trigger:", { workflowId: createdWorkflowId, cronExpression });
              await configureWorkflowSchedule(createdWorkflowId, {
                cron_expression: cronExpression,
                enabled: true
              });
              console.log("✅ Schedule trigger configured");
            } catch (scheduleErr) {
              const errorMsg = scheduleErr instanceof Error ? scheduleErr.message : String(scheduleErr);
              console.error("Schedule config error:", errorMsg);
              
              // Show retry dialog instead of just alerting
              setFailedTriggerWorkflowId(createdWorkflowId);
              setFailedTriggerConfig({
                cron: cronExpression,
                error: errorMsg
              });
              setTriggerRetryDialogOpen(true);
              setIsSaving(false);
              setSaveDialogOpen(false);
              return; // Stop execution to let user fix the issue
            }
          }
        }

        // Configure webhook triggers via API
        const webhookTriggers = triggerNodes.filter(node => node.data.config.triggerType === "webhook");
        for (const triggerNode of webhookTriggers) {
          const webhookConfig = triggerNode.data.config.webhookConfig;
          if (webhookConfig && webhookConfig.adapter_type && webhookConfig.secrets
              && Object.keys(webhookConfig.secrets).length > 0 && createdWorkflowId) {
            try {
              console.log("Configuring webhook trigger:", { 
                workflowId: createdWorkflowId, 
                adapter_type: webhookConfig.adapter_type 
              });
              
              await configureWorkflowWebhook(createdWorkflowId, {
                enabled: true,
                adapter_type: webhookConfig.adapter_type,
                secrets_ref: webhookConfig.secrets
              });
              
              console.log("✅ Webhook trigger configured");
            } catch (webhookErr) {
              const errorMsg = webhookErr instanceof Error ? webhookErr.message : String(webhookErr);
              console.error("Webhook config error:", errorMsg);
              
              // Show warning but continue (user can configure webhook manually later)
              showSnackbar(
                `Workflow created but webhook configuration failed: ${errorMsg}. You can configure it manually.`,
                "warning"
              );
            }
          }
        }
        
        if (webhookTriggers.length > 0) {
          console.log(`Configured ${webhookTriggers.length} webhook trigger(s)`);
        }
      }

      setSaveDialogOpen(false);
      
      // Success - show snackbar and navigate to workflow details page
      showSnackbar("Workflow saved successfully!", "success");
      
      // CLEAR DRAFT FROM localStorage AFTER SUCCESSFUL SAVE
      // The workflow is now persisted in the backend database, so the local draft
      // is no longer needed. This ensures users start fresh on the next visit.
      if (typeof window !== "undefined") {
        window.localStorage.removeItem(WORKFLOW_DRAFT_STORAGE_KEY);
      }
      navigate(`/workflow/${createdWorkflowId}`);
      
    } catch (err) {
      console.error("Failed to save workflow:", err);
      
      // If workflow was created but schedule config failed, provide recovery info
      if (createdWorkflowId) {
        alert(
          `Workflow created (ID: ${createdWorkflowId}) but there was an error:\n\n${err instanceof Error ? err.message : String(err)}\n\nThe workflow exists but may need manual configuration. Would you like to view it now?`
        );
        // Optionally navigate to the workflow
        if (window.confirm("Navigate to the workflow details page?")) {
          navigate(`/workflow/${createdWorkflowId}`);
        }
      } else {
        // Workflow creation failed completely
        alert(
          `Error creating workflow: ${
            err instanceof Error ? err.message : String(err)
          }`
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleConnectionSave = () => {
    if (!pendingConnection) return;

    // 1. Create the edges (one for each selected source)
    // Note: We need to filter out existing edges to avoid duplicates if necessary,
    // or ReactFlow handles it. We should ensure we add edges for ALL selected sources.

    const newEdges: Edge[] = [];
    selectedSourceIds.forEach((sourceId) => {
      // Create edge if it doesn't represent the pending connection (which we add manually below? NO)
      // Actually, we should just iterate and add edges.
      // Uniqueness check is good practice.
      newEdges.push({
        id: `e-${sourceId}-${pendingConnection.target}`,
        source: sourceId,
        target: pendingConnection.target!,
        type: "default",
        // markerEnd: { type: MarkerType.ArrowClosed },
      });
    });

    setEdges((eds) => {
      // Simple merge: add new edges, React Flow usually handles duplicates by ID
      // but let's be safe and filter.
      const existingIds = new Set(eds.map((e) => e.id));
      const textIds = newEdges.filter((e) => !existingIds.has(e.id));
      return [...eds, ...textIds];
    });

    // Correct way to update mappings with proper node lookup
    setNodes((currentNodes) => {
      return currentNodes.map((targetNode) => {
        if (targetNode.id === pendingConnection.target) {
          const config = targetNode.data.config;
          const newDependsOn = new Set(config.depends_on || []);
          const newInputMapping = { ...(config.input_mapping || {}) };

          selectedSourceIds.forEach((sourceId) => {
            newDependsOn.add(sourceId);

            // Find Source Node for Label
            const sourceNode = currentNodes.find((n) => n.id === sourceId);
            if (sourceNode) {
              const label = sourceNode.data.label;
              // Derive slug the same way generateSlug does (without uniqueness, that's fine for display)
              let slugBase = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
              if (!slugBase) slugBase = 'step';
              // Truncate to match STEP_ID_MAX_LENGTH (512) minus room for suffix
              if (slugBase.length > 509) slugBase = slugBase.substring(0, 509).replace(/_+$/, '');
              const key = slugBase + "_output";
              newInputMapping[key] = `$.steps.${sourceNode.id}.output`;
            }
          });

          const updatedNode = {
            ...targetNode,
            data: {
              ...targetNode.data,
              config: {
                ...config,
                depends_on: Array.from(newDependsOn),
                input_mapping: newInputMapping,
              },
            },
          };
          console.log("Updated Node (setNodes):", updatedNode);
          return updatedNode;
        }
        return targetNode;
      });
    });

    setConnectionDialogOpen(false);
    setPendingConnection(null);
  };

  const finalizeAddNode = () => {
    let label = "";

    if (selectedType === "trigger") {
      // Triggers are manual, they don't use the agents/tools lists
      if (formData.triggerType === "schedule") {
        // Use the helper function to generate label based on cron expression
        label = formData.cron ? getScheduleLabel(formData.cron) : "Daily Schedule";
      } else {
        // Webhook trigger - use adapter-specific label
        label = formData.webhookConfig?.adapter_type 
          ? getWebhookLabel(formData.webhookConfig.adapter_type)
          : "Webhook Trigger";
      }
    } else if (STRUCTURAL_NODE_DEFAULT_LABELS[selectedType!]) {
      // Structural node types get a meaningful default label
      label = STRUCTURAL_NODE_DEFAULT_LABELS[selectedType!];
    } else {
      // Standard Agent/Tool lookup
      const list = selectedType === "agent" ? agents : tools;
      const selectedItem = list.find(
        (i) => (i.id || i.agent_id || i.tool_id) === formData.id
      );
      label = selectedItem?.display_name || formData.id || "New Step";
    }

    // Use utility function to create new node
    const newNode = createWorkflowNode(selectedType!, formData, label, nodes.length);

    setNodes((nds) => [...nds, newNode]);
    handleClose();
  };

  return (
    <Box sx={{ pt: 1, px: 3 }}>
      <Toolbar
        disableGutters
        sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}
      >
        <Typography variant="h4" sx={{ pl: 0, mr: 2 }}>
          Workflow Generator
        </Typography>

        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          color="primary"
          onClick={() => { setGlobalTimeoutSeconds(calculateGlobalTimeout()); setSaveDialogOpen(true); }}
          disabled={isSaving || nodes.length === 0}
        >
          Save Workflow
        </Button>
      </Toolbar>
      <Paper sx={{ p: 3 }}>
        <Box
          sx={{
            width: "100%",
            height: "600px",
            border: "1px solid #e0e0e0",
            position: "relative",
          }}
        >
          <ReactFlow
            nodes={nodes}
            edges={displayedEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            defaultEdgeOptions={defaultEdgeOptions}
            nodeTypes={nodeTypes}
            fitView={false}
            defaultViewport={{ x: 50, y: 50, zoom: 0.9 }}
          >
            <Background variant={BackgroundVariant.Dots} />
            <Controls />

            <Panel position="top-left">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Fab color="primary" size="small" onClick={handleOpen}>
                  <AddIcon />
                </Fab>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showDataMappings}
                      onChange={(e) => setShowDataMappings(e.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '11px', color: '#64748B', userSelect: 'none' }}>
                      Show data mappings
                    </Typography>
                  }
                  sx={{
                    ml: 0.5,
                    bgcolor: 'rgba(255,255,255,0.9)',
                    borderRadius: '8px',
                    px: 1,
                    py: 0.25,
                    border: '1px solid #e2e8f0',
                  }}
                />

              </Box>
              <AddNodePopover
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                selectedType={selectedType}
                onTypeSelect={handleTypeSelect}
                onBackClick={() => setSelectedType(null)}
                formData={formData}
                onFormDataChange={(updates) =>
                  setFormData((prev) => ({ ...prev, ...updates }))
                }
                agents={agents}
                tools={tools}
                onAddNode={finalizeAddNode}
                nodes={nodes}
                edges={edges}
                setNodes={setNodes}
                setEdges={setEdges}
              />
            </Panel>
          </ReactFlow>
        </Box>

        {/* SMART CONNECTION DIALOG */}
        <SmartConnectionDialog
          open={smartDialogOpen}
          onClose={() => setSmartDialogOpen(false)}
          sourceNodes={smartDialogSources}
          targetNode={smartDialogTarget}
          nodes={nodes}
          onSave={handleSmartConnectionSave}
        />

        {/* CONNECTION CONFIG DIALOG (Legacy - kept for compatibility) */}
        <ConnectionDialog
          open={connectionDialogOpen}
          onClose={() => setConnectionDialogOpen(false)}
          nodes={nodes}
          targetNodeId={pendingConnection?.target || null}
          selectedSourceIds={selectedSourceIds}
          onSourceIdsChange={setSelectedSourceIds}
          onSave={handleConnectionSave}
        />

        {/* SAVE WORKFLOW DIALOG */}
        <Dialog
          open={saveDialogOpen}
          onClose={() => setSaveDialogOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Save Workflow</DialogTitle>
          <DialogContent>
            <Typography
              variant="body2"
              color="text.secondary"
              paragraph
              sx={{ mt: 1 }}
            >
              Provide details for your new workflow.
            </Typography>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField
                label="Workflow Display Name"
                fullWidth
                required
                value={workflowDisplayName}
                onChange={(e) => {
                  setWorkflowDisplayName(e.target.value);
                  // Auto-update ID only if user hasn't manually changed it
                  if (workflowName === sanitizeWorkflowName(workflowDisplayName)) {
                    setWorkflowName(sanitizeWorkflowName(e.target.value));
                  }
                }}
                placeholder="e.g. Marketing Campaign Generator"
                helperText="Human-readable name for this workflow"
              />
              <TextField
                label="Workflow ID"
                fullWidth
                required
                value={workflowName}
                onChange={(e) => setWorkflowName(sanitizeWorkflowName(e.target.value))}
                placeholder="e.g. marketing-campaign-generator"
                helperText="Unique identifier (lowercase, hyphens only). Multiple workflows can have the same display name but must have unique IDs."
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={workflowDescription}
                onChange={(e) => setWorkflowDescription(e.target.value)}
                placeholder="Describe what this workflow does..."
              />
              <TextField
                label="Workflow Timeout (seconds)"
                fullWidth
                type="number"
                value={globalTimeoutSeconds}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 1800;
                  setGlobalTimeoutSeconds(Math.min(Math.max(val, 1800), 86400));
                }}
                helperText="Auto-calculated from step timeouts. Min 1800s (30 min), max 86400s (24 hr)."
                inputProps={{ min: 1800, max: 86400 }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setSaveDialogOpen(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveWorkflow}
              variant="contained"
              disabled={isSaving || !workflowDisplayName.trim() || !workflowName.trim()}
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* TRIGGER CONFIG RETRY DIALOG */}
        <Dialog
          open={triggerRetryDialogOpen}
          onClose={() => {}} // Prevent closing by clicking outside
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Schedule Trigger Configuration Failed</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="error" paragraph sx={{ mt: 1 }}>
              <strong>Error:</strong> {failedTriggerConfig?.error}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              The workflow was created successfully (ID: {failedTriggerWorkflowId}), 
              but there was an error configuring the schedule trigger.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              You can update the cron expression below and retry, or skip this step 
              and configure it manually later.
            </Typography>
            
            {failedTriggerConfig && (
              <Box sx={{ mt: 2 }}>
                <CronScheduleBuilder
                  value={failedTriggerConfig.cron}
                  onChange={(newCron) => {
                    setFailedTriggerConfig(prev => 
                      prev ? { ...prev, cron: newCron } : null
                    );
                  }}
                  showValidation={true}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleSkipTriggerConfig}
              disabled={retryingTrigger}
            >
              Skip & View Workflow
            </Button>
            <Button
              onClick={handleRetryTriggerConfig}
              variant="contained"
              disabled={retryingTrigger || !failedTriggerConfig?.cron}
            >
              {retryingTrigger ? "Retrying..." : "Retry"}
            </Button>
          </DialogActions>
        </Dialog>

        <WorkflowPropertiesDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          selectedElement={selectedElement}
          nodes={nodes}
          onUpdateNode={(nodeId, updates) => {
            setNodes((nds) =>
              nds.map((node) =>
                node.id === nodeId
                  ? { ...node, data: { ...node.data, ...updates } as WorkflowNodeData }
                  : node
              )
            );
            // Keep selectedElement in sync so the drawer re-initializes with the saved state
            setSelectedElement((prev) => {
              if (prev && 'data' in prev && prev.id === nodeId) {
                return { ...prev, data: { ...prev.data, ...updates } as WorkflowNodeData };
              }
              return prev;
            });
          }}
        />

        {/* SUCCESS SNACKBAR */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
};

export default WorkflowGeneratorPage;
